import { createAsyncThunk } from '@reduxjs/toolkit';
import { createApiRequest } from '../../utils/apiUtils';
import {
  AvailabilitySlot,
  BusinessCalendarDay,
  CreateReservationResult,
  DashboardGranularity,
  DashboardResponse,
  GuestHistoryView,
  ReservationDetailView,
  ReservationInput,
  ReservationListItem,
  ReservationSeatType,
  ReservationStatusAction,
  TimelineView,
} from '../../types/reservation';

/**
 * スタッフ予約管理の API 群。
 * サブスク切れでも既存予約の処理ができる要件のため、ここにはサブスクゲート系の制御を入れない。
 * ポーリング禁止（一覧は表示時取得+手動更新のみ）。
 */

// 予約一覧（status/from/to/q は任意。q は氏名/電話/予約番号の部分一致）
export const fetchReservations = createAsyncThunk<
  ReservationListItem[],
  { storeId: string; status?: string; from?: string; to?: string; q?: string }
>('reservation/fetchReservations', async ({ storeId, status, from, to, q }, { rejectWithValue }) => {
  try {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (q) params.set('q', q);
    const qs = params.toString();
    return await createApiRequest<ReservationListItem[]>(
      `/stores/${storeId}/reservations${qs ? `?${qs}` : ''}`,
      { method: 'GET', requiresAuth: true },
    );
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

// 予約詳細
export const fetchReservationDetail = createAsyncThunk<
  ReservationDetailView,
  { storeId: string; reservationId: string }
>('reservation/fetchReservationDetail', async ({ storeId, reservationId }, { rejectWithValue }) => {
  try {
    return await createApiRequest<ReservationDetailView>(
      `/stores/${storeId}/reservations/${reservationId}`,
      { method: 'GET', requiresAuth: true },
    );
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

// 台帳（席タイプ別タイムライン）
export const fetchTimeline = createAsyncThunk<TimelineView, { storeId: string; date: string }>(
  'reservation/fetchTimeline',
  async ({ storeId, date }, { rejectWithValue }) => {
    try {
      return await createApiRequest<TimelineView>(
        `/stores/${storeId}/reservations/timeline?date=${encodeURIComponent(date)}`,
        { method: 'GET', requiresAuth: true },
      );
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

// 予約ダッシュボード集計（統合契約 B）。
// granularity=HOUR|DAY|MONTH と anchor(YYYY-MM-DD) を基準にバケット別 status 集計を取得する。
// 集計値のみ（個人情報を返さない）ため過去も参照可。BE 実装中のため後方互換で消費する。
export const fetchReservationDashboard = createAsyncThunk<
  DashboardResponse,
  { storeId: string; granularity: DashboardGranularity; anchor: string }
>(
  'reservation/fetchReservationDashboard',
  async ({ storeId, granularity, anchor }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ granularity, anchor });
      return await createApiRequest<DashboardResponse>(
        `/stores/${storeId}/reservations/dashboard?${params.toString()}`,
        { method: 'GET', requiresAuth: true },
      );
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

// ステータス遷移（CONFIRM/REJECT/CANCEL/COMPLETE/NO_SHOW）
export const changeReservationStatus = createAsyncThunk<
  void,
  { storeId: string; reservationId: string; action: ReservationStatusAction }
>(
  'reservation/changeReservationStatus',
  async ({ storeId, reservationId, action }, { rejectWithValue }) => {
    try {
      await createApiRequest(`/stores/${storeId}/reservations/${reservationId}/status`, {
        method: 'PATCH',
        requiresAuth: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

// スタッフ代理作成（force=true で満枠/営業時間外でも警告のみで登録）
export const createStaffReservation = createAsyncThunk<
  CreateReservationResult,
  { storeId: string; input: ReservationInput; force: boolean }
>('reservation/createStaffReservation', async ({ storeId, input, force }, { rejectWithValue }) => {
  try {
    return await createApiRequest<CreateReservationResult>(`/stores/${storeId}/reservations`, {
      method: 'POST',
      requiresAuth: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...input, force }),
    });
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

// 予約内容変更（force 再送対応）
export const changeReservationDetails = createAsyncThunk<
  void,
  { storeId: string; reservationId: string; input: ReservationInput; force: boolean }
>(
  'reservation/changeReservationDetails',
  async ({ storeId, reservationId, input, force }, { rejectWithValue }) => {
    try {
      await createApiRequest(`/stores/${storeId}/reservations/${reservationId}`, {
        method: 'PUT',
        requiresAuth: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...input, force }),
      });
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

// スタッフメモ更新（顧客非公開）
export const updateStaffNote = createAsyncThunk<
  { staffNote: string | null },
  { storeId: string; reservationId: string; staffNote: string | null }
>(
  'reservation/updateStaffNote',
  async ({ storeId, reservationId, staffNote }, { rejectWithValue }) => {
    try {
      await createApiRequest(`/stores/${storeId}/reservations/${reservationId}/staff-note`, {
        method: 'PATCH',
        requiresAuth: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ staffNote }),
      });
      return { staffNote };
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

// 席割当変更（null=割当解除。満卓かつ force=false は 422、force=true でオーバーブック可）
export const updateSeatAssignment = createAsyncThunk<
  void,
  {
    storeId: string;
    reservationId: string;
    seatTypeId: string | null;
    force: boolean;
    /** 消費卓数（席の連結時に2以上を明示指定。省略時は BE が自動決定） */
    units?: number;
  }
>(
  'reservation/updateSeatAssignment',
  async ({ storeId, reservationId, seatTypeId, force, units }, { rejectWithValue }) => {
    try {
      await createApiRequest(`/stores/${storeId}/reservations/${reservationId}/seat-assignment`, {
        method: 'PUT',
        requiresAuth: true,
        headers: { 'Content-Type': 'application/json' },
        // units は指定時のみ送る（未指定=BE 自動割当）
        body: JSON.stringify({ seatTypeId, force, ...(units != null ? { units } : {}) }),
      });
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

// 顧客履歴（同一メール/電話の過去実績）
export const fetchGuestHistory = createAsyncThunk<
  GuestHistoryView,
  { storeId: string; reservationId: string }
>('reservation/fetchGuestHistory', async ({ storeId, reservationId }, { rejectWithValue }) => {
  try {
    return await createApiRequest<GuestHistoryView>(
      `/stores/${storeId}/reservations/${reservationId}/guest-history`,
      { method: 'GET', requiresAuth: true },
    );
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

// 確認メール再送
export const resendConfirmation = createAsyncThunk<
  void,
  { storeId: string; reservationId: string }
>('reservation/resendConfirmation', async ({ storeId, reservationId }, { rejectWithValue }) => {
  try {
    await createApiRequest(`/stores/${storeId}/reservations/${reservationId}/resend-confirmation`, {
      method: 'POST',
      requiresAuth: true,
    });
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

// 手動返金（任意額）
export const refundReservation = createAsyncThunk<
  void,
  { storeId: string; reservationId: string; amount: number }
>('reservation/refundReservation', async ({ storeId, reservationId, amount }, { rejectWithValue }) => {
  try {
    await createApiRequest(`/stores/${storeId}/reservations/${reservationId}/refund`, {
      method: 'POST',
      requiresAuth: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount }),
    });
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

// 空き状況（スタッフ・代理登録の SlotPicker 用。partySize 任意）
export const fetchStaffAvailability = createAsyncThunk<
  AvailabilitySlot[],
  { storeId: string; from: string; to: string; partySize?: number }
>(
  'reservation/fetchStaffAvailability',
  async ({ storeId, from, to, partySize }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ from, to });
      if (partySize) params.set('partySize', String(partySize));
      return await createApiRequest<AvailabilitySlot[]>(
        `/stores/${storeId}/availability?${params.toString()}`,
        { method: 'GET', requiresAuth: true },
      );
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

// 営業カレンダー（スタッフ・代理登録の日付選択用）
export const fetchStaffBusinessCalendar = createAsyncThunk<
  BusinessCalendarDay[],
  { storeId: string; from: string; to: string }
>(
  'reservation/fetchStaffBusinessCalendar',
  async ({ storeId, from, to }, { rejectWithValue }) => {
    try {
      return await createApiRequest<BusinessCalendarDay[]>(
        `/stores/${storeId}/business-calendar?from=${from}&to=${to}`,
        { method: 'GET', requiresAuth: true },
      );
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

// 席タイプ一覧（詳細画面の席割当変更・CLERK も参照可）
export const fetchSeatTypesForAssignment = createAsyncThunk<ReservationSeatType[], string>(
  'reservation/fetchSeatTypesForAssignment',
  async (storeId, { rejectWithValue }) => {
    try {
      return await createApiRequest<ReservationSeatType[]>(
        `/stores/${storeId}/reservation-seat-types`,
        { method: 'GET', requiresAuth: true },
      );
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

// コース一覧（代理登録・変更ダイアログのコース選択用）
export const fetchReservationMenusForStaff = createAsyncThunk<
  import('../../types/reservation').ReservationMenu[],
  string
>('reservation/fetchReservationMenusForStaff', async (storeId, { rejectWithValue }) => {
  try {
    return await createApiRequest<import('../../types/reservation').ReservationMenu[]>(
      `/stores/${storeId}/reservation-menus`,
      { method: 'GET', requiresAuth: true },
    );
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

// 質問一覧（代理登録の動的質問用）
export const fetchInquiriesForStaff = createAsyncThunk<
  import('../../types/reservation').ReservationInquiry[],
  string
>('reservation/fetchInquiriesForStaff', async (storeId, { rejectWithValue }) => {
  try {
    return await createApiRequest<import('../../types/reservation').ReservationInquiry[]>(
      `/stores/${storeId}/reservation-inquiries`,
      { method: 'GET', requiresAuth: true },
    );
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});
